import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { FileText, Loader2 } from 'lucide-react';
import { certificationAgreementService, AgreementType, AgreementVersion } from '@/services/certificationAgreementService';

interface CertificationDocumentViewerProps {
  agreementType: AgreementType;
  open: boolean;
  onClose: () => void;
}

const agreementTitles: Record<AgreementType, string> = {
  platform_participation: 'Platform Participation Agreement',
  brand_usage: 'Brand Usage Guidelines',
  certification_terms: 'Certification Terms'
};

export const CertificationDocumentViewer = ({
  agreementType,
  open,
  onClose
}: CertificationDocumentViewerProps) => {
  const [content, setContent] = useState<AgreementVersion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadContent();
    }
  }, [open, agreementType]);

  const loadContent = async () => {
    setLoading(true);
    const data = await certificationAgreementService.getAgreementContent(agreementType);
    setContent(data);
    setLoading(false);
  };

  const formatContent = (text: string) => {
    // Split by double newlines for paragraphs and format
    return text.split('\n\n').map((paragraph, index) => {
      // Check if it's a header (starts with a number followed by period)
      const isHeader = /^\d+\.\s/.test(paragraph);
      const isBullet = paragraph.startsWith('•');
      
      if (isHeader) {
        return (
          <h3 key={index} className="text-base font-semibold text-foreground mt-6 mb-2">
            {paragraph}
          </h3>
        );
      }
      
      if (isBullet || paragraph.includes('\n•')) {
        const lines = paragraph.split('\n');
        return (
          <ul key={index} className="list-none space-y-1 my-2">
            {lines.map((line, lineIndex) => (
              <li key={lineIndex} className="text-sm text-muted-foreground">
                {line}
              </li>
            ))}
          </ul>
        );
      }
      
      return (
        <p key={index} className="text-sm text-muted-foreground mb-3">
          {paragraph}
        </p>
      );
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-foreground">
            <FileText className="h-5 w-5 text-primary" />
            {agreementTitles[agreementType]}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-140px)] pr-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : content ? (
            <div className="prose prose-sm max-w-none">
              {formatContent(content.content)}
              
              <div className="mt-6 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Version: {content.version} | Effective Date: {new Date(content.effective_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Unable to load agreement content. Please try again.
            </p>
          )}
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t border-border">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
